import { startTransition } from "react"

import eq from "lodash-es/eq"
import first from "lodash-es/first"
import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import isString from "lodash-es/isString"
import map from "lodash-es/map"
import upperFirst from "lodash-es/upperFirst"

import type { TTFunction } from "$/i18n/i18n"

import { ETypographyColor, ETypographySize, ETypographyWeight } from "@/3514/components"
import { type TScreenBreakpoints, sessionTimeStateUtils } from "@/3514/hooks"
import {
    coachCalendarRelinkAccountsPagePreloader,
    coachCalendarRelinkAccountsQueryPrefetcher
} from "@/3514/pages/coach.calendar"
import type { ICoachPaginatedSessionModel as ISession, ISessionBaseModel } from "@/3514/types"
import type { IUseTranslation } from "@/hooks"

import type { ICoachDashboardParticipantsQueryParticipantModel as IParticipant } from "./coach.dashboard.api.types"
import {
    ECoachDashboardActiveTab as EActiveTab,
    ECoachDashboardStaticNotificationType as ENotificationType,
    ECoachDashboardTableCellType as ETableCellType,
    ECoachDashboardTableColumnName as ETableColumnName,
    ECoachDashboardTableDataType as ETableType,
    type TCoachDashboardStaticNotificationConfig as TNotificationConfig,
    type TCoachDashboardRenderStaticNotificationConfigFunctionParams as TRenderStaticNotificationConfigFunctionParams,
    type TCoachDashboardRenderTableHeaderFunction as TRenderTableFunction,
    type TCoachDashboardStaticNotificationsMap as TStaticNotificationsMap,
    type TCoachDashboardTableCellProps as TTableCellProps,
    type TCoachDashboardTableColumnConfigRenderer as TTableColumnConfigRenderer,
    type TCoachDashboardTableColumnRenderConfigParams as TTableColumnRenderConfigParams,
    type TCoachDashboardTableColumnRenderConfigsMap as TTableColumnRenderConfigsMap,
    type TCoachDashboardTableHeader as TTableHeader,
    type TCoachDashboardTableHeadersRenderMap as TTableHeaderRenderMap,
    type TCoachDashboardTableRowProps as TTableRowProps,
    type TCoachDashboardTableRowRenderConfigsMap as TTableRowRenderConfigsMap
} from "./coach.dashboard.ui.types"
import uiUtils from "./coach.dashboard.ui.utils"

const markedAttendancesVirtualListConfig = {
    visibleItemCount: 1,
    itemCountToRenderExpandButton: 1
}

const {
    getTableRowItemDate,
    getTableRowItemTime,
    getTableRowItemYearDate,
    getTableRowAvatarsList,
    getPanelTimezone: getTimezone
} = uiUtils

const headerInputId: string = "coach-dashboard-data-panel-input"

function renderStatiNotificationsConfig(): TStaticNotificationsMap {
    return {
        [ENotificationType.SetUpCalendar]: (
            params: TRenderStaticNotificationConfigFunctionParams
        ): TNotificationConfig => ({
            isVisible: params.getIsBoxVisible(ENotificationType.SetUpCalendar),
            title: params.t("coachSide.dashboard.notifications.calendars.title"),
            description: params.t("coachSide.dashboard.notifications.calendars.description"),
            actionLabel: params.t("coachSide.dashboard.notifications.calendars.action"),
            action: (): void => startTransition((): void => params.action(ENotificationType.SetUpCalendar))
        }),
        [ENotificationType.RelinkCalendar]: (
            params: TRenderStaticNotificationConfigFunctionParams
        ): TNotificationConfig => ({
            isVisible: params.getIsBoxVisible(ENotificationType.RelinkCalendar),
            title: params.t("coachSide.dashboard.notifications.relink.title"),
            description: params.t("coachSide.dashboard.notifications.relink.description"),
            actionLabel: params.t("coachSide.dashboard.notifications.relink.action"),
            action: (): void => startTransition((): void => params.action(ENotificationType.RelinkCalendar)),
            hover: async (): Promise<void> => {
                void coachCalendarRelinkAccountsPagePreloader()
                void coachCalendarRelinkAccountsQueryPrefetcher({ qc: params.qc })
            }
        })
    }
}

function renderHeaderTabsConfig(params: { t: TTFunction; activeTab: EActiveTab }) {
    return [
        {
            id: EActiveTab.Sessions,
            label: params.t("coachSide.dashboard.dataPanel.header.tabs.sessions"),
            textSize: ETypographySize.Heading2,
            textWeight: ETypographyWeight.Medium,
            textColor: eq(params.activeTab, EActiveTab.Sessions) ? ETypographyColor.Blue200 : ETypographyColor.Dark
        },
        {
            id: EActiveTab.Participants,
            label: params.t("coachSide.dashboard.dataPanel.header.tabs.participants"),
            textSize: ETypographySize.Heading2,
            textWeight: ETypographyWeight.Medium,
            textColor: eq(params.activeTab, EActiveTab.Participants) ? ETypographyColor.Blue200 : ETypographyColor.Dark
        }
    ]
}

const tabTableTypesMap: { [A in EActiveTab]: ETableType } = {
    [EActiveTab.Sessions]: ETableType.Sessions,
    [EActiveTab.Participants]: ETableType.Participants
}

function renderTableHeaderColumnsConfig(): TTableHeaderRenderMap {
    return {
        [EActiveTab.Sessions]: (params: {
            breakpoints: TScreenBreakpoints
            translation: IUseTranslation
        }): TRenderTableFunction[] => [
            (): TTableHeader => ({
                name: ETableColumnName.Sessions_DateTime,
                isSortable: true,
                isVisible: true,
                label:
                    params.breakpoints.xs || params.breakpoints.sm
                        ? params.translation.t("coachSide.dashboard.dataPanel.table.sessions.column.dateTimeTZ", {
                              zone: getTimezone()
                          })
                        : params.translation.t("coachSide.dashboard.dataPanel.table.sessions.column.dateTime")
            }),
            (): TTableHeader => ({
                name: ETableColumnName.Sessions_Cohort,
                isSortable: false,
                isVisible: !(params.breakpoints.xs || params.breakpoints.sm),
                label: params.translation.t("coachSide.dashboard.dataPanel.table.sessions.column.cohort")
            }),
            (): TTableHeader => ({
                name: ETableColumnName.Sessions_SessionType,
                isSortable: false,
                isVisible: !(params.breakpoints.xs || params.breakpoints.sm),
                label: params.translation.t("coachSide.dashboard.dataPanel.table.sessions.column.sessionType")
            }),
            (): TTableHeader => ({
                name: ETableColumnName.Sessions_Participants,
                isSortable: false,
                isVisible: !(params.breakpoints.xs || params.breakpoints.sm),
                label: params.translation.t("coachSide.dashboard.dataPanel.table.sessions.column.participants")
            })
        ],
        [EActiveTab.Participants]: (params: {
            breakpoints: TScreenBreakpoints
            translation: IUseTranslation
        }): TRenderTableFunction[] => [
            (): TTableHeader => ({
                name: ETableColumnName.Participants_Avatar,
                isSortable: false,
                isVisible: !(params.breakpoints.xs || params.breakpoints.sm),
                label: null
            }),
            (): TTableHeader => ({
                name: ETableColumnName.Participants_Name,
                isSortable: true,
                isVisible: true,
                label: params.translation.t("coachSide.dashboard.dataPanel.table.participants.column.name")
            }),
            (): TTableHeader => ({
                name: ETableColumnName.Participants_Cohort,
                isSortable: false,
                isVisible: !(params.breakpoints.xs || params.breakpoints.sm),
                label: params.translation.t("coachSide.dashboard.dataPanel.table.participants.column.cohort")
            }),
            (): TTableHeader => ({
                name: ETableColumnName.Participants_Program,
                isSortable: false,
                isVisible: !(params.breakpoints.xs || params.breakpoints.sm),
                label: params.translation.t("coachSide.dashboard.dataPanel.table.participants.column.program")
            }),
            (): TTableHeader => ({
                name: ETableColumnName.Participants_Sessions,
                isSortable: true,
                isVisible: !(params.breakpoints.xs || params.breakpoints.sm),
                label: params.translation.t("coachSide.dashboard.dataPanel.table.participants.column.sessions")
            })
        ]
    }
}

function renderTableColumnsRenderConfig(): TTableColumnRenderConfigsMap {
    return {
        [ETableType.Sessions]: <TData extends ISession = ISession>(
            params: TTableColumnRenderConfigParams
        ): TTableColumnConfigRenderer<TData>[] => {
            const { breakpoints }: TTableColumnRenderConfigParams = params
            const { t }: IUseTranslation = params.translation

            return [
                (session: TData): TTableCellProps => ({
                    ...params,
                    index: 0,
                    isVisible: true,
                    type: ETableCellType.AccentText,
                    title: getTableRowItemDate(session.sessionTime, {
                        // NB: read through the namespace at call time - destructuring at module
                        // scope would hit the TDZ of this circular import graph.
                        withYear: sessionTimeStateUtils.getIsSessionPast({
                            startDate: session.sessionTime,
                            durationInMins: session.sessionDuration
                        }),
                        withShortYear: breakpoints.md
                    }),
                    subTitle: getTableRowItemTime(session.sessionTime)
                }),
                (session: TData): TTableCellProps => ({
                    ...params,
                    index: 1,
                    isVisible: true,
                    type: ETableCellType.DetailedText,
                    title: session.cohortName,
                    subTitle: upperFirst(session.cohortModality)
                }),
                (session: TData): TTableCellProps => ({
                    ...params,
                    index: 2,
                    isVisible: !(breakpoints.xs || breakpoints.sm),
                    type: ETableCellType.DetailedText,
                    title: session.sessionName,
                    subTitle: `${session.sessionDuration}${t("common.min")}`
                }),
                (session: TData): TTableCellProps => ({
                    ...params,
                    index: 3,
                    isVisible: !(breakpoints.xs || breakpoints.sm),
                    type: ETableCellType.Avatars,
                    participants: getTableRowAvatarsList(session.participants, session.extraParties),
                    sessionJoinUrl: session.joinUrl,
                    sessionDuration: session.sessionDuration,
                    sessionTime: session.sessionTime
                })
            ]
        },
        [ETableType.Participants]: <TData extends IParticipant = IParticipant>(
            params: TTableColumnRenderConfigParams
        ): TTableColumnConfigRenderer<TData>[] => {
            const { breakpoints }: TTableColumnRenderConfigParams = params
            const { t }: IUseTranslation = params.translation

            return [
                (participant: TData): TTableCellProps => ({
                    ...params,
                    index: 0,
                    isVisible: !(breakpoints.xs || breakpoints.sm),
                    type: ETableCellType.SingleAvatar,
                    userData: { firstName: participant.firstName, lastName: participant.lastName },
                    url: participant.photo
                }),
                (participant: TData): TTableCellProps => ({
                    ...params,
                    index: 1,
                    isVisible: true,
                    type: ETableCellType.AccentText,
                    title: participant.firstName,
                    subTitle: participant.lastName
                }),
                (participant: TData): TTableCellProps => ({
                    ...params,
                    index: 2,
                    isVisible: !(breakpoints.xs || breakpoints.sm),
                    type: ETableCellType.DetailedText,
                    title: participant.cohortName,
                    subTitle: isEmpty(participant.upcomingSessions)
                        ? t("common.none")
                        : upperFirst(participant.upcomingSessions[0].cohortModality)
                }),
                (participant: TData): TTableCellProps => ({
                    ...params,
                    index: 3,
                    isVisible: true,
                    type: ETableCellType.SingleText,
                    subTitle: isEmpty(participant.upcomingSessions)
                        ? t("common.none")
                        : upperFirst(participant.upcomingSessions[0].productTypeInternalName)
                }),
                (participant: TData): TTableCellProps => {
                    const _firstUpcomingSession: ISessionBaseModel | undefined = first<ISessionBaseModel>(
                        participant.upcomingSessions
                    )

                    return {
                        ...params,
                        index: 4,
                        isVisible: true,
                        type: ETableCellType.DetailedText,
                        title: t("common.nextSession"),
                        subTitle:
                            isEmpty(participant.upcomingSessions) ||
                            isNull(_firstUpcomingSession?.sessionTime) ||
                            !isString(_firstUpcomingSession?.sessionTime)
                                ? t("common.none")
                                : getTableRowItemYearDate(_firstUpcomingSession.sessionTime)
                    }
                }
            ]
        }
    }
}

function renderTableRowsRenderConfig(): TTableRowRenderConfigsMap {
    return {
        [ETableType.Sessions]: <TData extends ISession = ISession>(props: {
            data: TData
            columnsRenderers: TTableColumnConfigRenderer<TData>[]
        }): TTableRowProps<TData, "sessionId"> => {
            return {
                indexKey: "sessionId",
                type: ETableType.Sessions,
                dataSource: props.data,
                cellPropsRenderers: map<TTableColumnConfigRenderer<TData>, TTableCellProps>(
                    props.columnsRenderers,
                    (renderer: TTableColumnConfigRenderer<TData>): TTableCellProps => renderer(props.data)
                )
            }
        },
        [ETableType.Participants]: <TData extends IParticipant = IParticipant>(props: {
            data: TData
            columnsRenderers: TTableColumnConfigRenderer<TData>[]
        }): TTableRowProps<TData, "id"> => {
            return {
                indexKey: "id",
                type: ETableType.Participants,
                dataSource: props.data,
                cellPropsRenderers: map<TTableColumnConfigRenderer<TData>, TTableCellProps>(
                    props.columnsRenderers,
                    (renderer: TTableColumnConfigRenderer<TData>): TTableCellProps => renderer(props.data)
                )
            }
        }
    }
}

const TABLE_FILLER: string = "..."

const TABLE_DEFAULT_PAGINATION_PAGE: number = 1

export default {
    markedAttendancesVirtualListConfig,
    headerInputId,
    renderStatiNotificationsConfig,
    renderHeaderTabsConfig,
    tabTableTypesMap,
    renderTableHeaderColumnsConfig,
    renderTableColumnsRenderConfig,
    renderTableRowsRenderConfig,
    TABLE_FILLER,
    TABLE_DEFAULT_PAGINATION_PAGE
}
